/**
 * Google Identity Services (GIS) helpers — JWT decode, payload mapping, error messages.
 * Replaces legacy react-google-login / profileObj handling.
 */

/**
 * @typedef {'invalid'|'expired'|'cancelled'|'network'|'unknown'} GoogleAuthErrorCode
 */

/**
 * Decode GIS credential JWT and validate required claims.
 * @param {string} credential
 * @returns {{ ok: true, payload: object } | { ok: false, code: GoogleAuthErrorCode, message: string }}
 */
export function decodeGoogleCredentialJwt(credential) {
  if (!credential || typeof credential !== "string") {
    return {
      ok: false,
      code: "invalid",
      message: "Invalid Google credential. Please try again.",
    };
  }

  try {
    const parts = credential.split(".");
    if (parts.length < 2) {
      return {
        ok: false,
        code: "invalid",
        message: "Invalid Google credential. Please try again.",
      };
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const jsonPayload = decodeURIComponent(
      atob(padded)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    const payload = JSON.parse(jsonPayload);

    if (!payload || !payload.sub) {
      return {
        ok: false,
        code: "invalid",
        message: "Invalid Google credential. Please try again.",
      };
    }

    if (payload.exp && Number(payload.exp) * 1000 < Date.now()) {
      return {
        ok: false,
        code: "expired",
        message: "Your Google sign-in expired. Please try again.",
      };
    }

    return { ok: true, payload: payload };
  } catch (e) {
    return {
      ok: false,
      code: "invalid",
      message: "Invalid Google credential. Please try again.",
    };
  }
}

/**
 * Map decoded JWT claims + raw GIS credential to the Laravel v4/register social payload.
 * Server verifies `id_token` cryptographically (LQI-C02); social_unique_id is still sent for
 * compatibility but is overwritten from verified claims on the API.
 *
 * @param {object} payload
 * @param {string} deviceToken
 * @param {string} timezone
 * @param {string} idToken raw GIS credential JWT (required)
 * @returns {object}
 * @throws {Error} when idToken is missing
 */
export function mapGoogleJwtToRegisterPayload(payload, deviceToken, timezone, idToken) {
  const token = idToken === undefined || idToken === null ? "" : String(idToken).trim();
  if (!token) {
    throw new Error("Google ID token is required for Google sign-in.");
  }

  return {
    social_unique_id: payload.sub,
    login_by: "google",
    email: payload.email || "",
    name: payload.name || "",
    picture: payload.picture || "",
    device_type: "web",
    device_token: deviceToken,
    timezone: timezone,
    // Always include — backend SocialLoginVerificationService requires this field.
    // Send GIS aliases together so older proxies / parsers that drop one still verify.
    id_token: token,
    google_id_token: token,
    credential: token,
  };
}

/**
 * Restrict post-login redirects to same-origin relative paths (open-redirect hardening).
 * @param {*} locationState react-router location.state
 * @param {string} [fallback="/view-profiles"]
 * @returns {string}
 */
export function safeAuthRedirectPath(locationState, fallback) {
  const defaultPath = fallback || "/view-profiles";
  const from = locationState && locationState.from;
  const pathname = from && typeof from.pathname === "string" ? from.pathname : "";
  if (!pathname || pathname.charAt(0) !== "/" || pathname.indexOf("//") === 0) {
    return defaultPath;
  }
  // Block protocol-relative and external absolute URLs stuffed into pathname.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(pathname)) {
    return defaultPath;
  }
  const search = from && typeof from.search === "string" ? from.search : "";
  return pathname + search;
}

/**
 * User-facing messages for GIS / popup failures.
 * @param {GoogleAuthErrorCode|string} code
 * @returns {string}
 */
export function googleAuthErrorMessage(code) {
  switch (code) {
    case "cancelled":
      return "Google sign-in was cancelled.";
    case "expired":
      return "Your Google sign-in expired. Please try again.";
    case "invalid":
      return "Invalid Google credential. Please try again.";
    case "network":
      return "Network error during Google sign-in. Please try again.";
    default:
      return "Google sign-in failed. Please try again.";
  }
}

/**
 * Normalize axios / fetch failures for toast display.
 * @param {*} error
 * @returns {string}
 */
export function googleAuthNetworkErrorMessage(error) {
  if (!error) {
    return googleAuthErrorMessage("network");
  }
  if (typeof error === "string") {
    return error;
  }
  if (error.response && error.response.data) {
    const data = error.response.data;
    if (data.error_messages) {
      return data.error_messages;
    }
    if (data.message) {
      return data.message;
    }
  }
  if (error.message) {
    return error.message;
  }
  return googleAuthErrorMessage("network");
}
